import { LangCode } from "src/requests/localization/types";
import { ValueFormat } from "src/requests/templates/types";
import getEnrichedValueWithDelimiterAndZeros from "./getEnrichedValueWithDelimiterAndZeros";

export const isNumberDecimal = (format?: ValueFormat): boolean =>
  format?.typeSpecific.number?.roundTo === 0;

export const getSeparatorForLocalization = (locale: LangCode): string =>
  locale === "en" ? "." : ",";

const getThousandsSeparator = (format?: ValueFormat): string => {
  const separator = format?.typeSpecific.number?.thousandsSeparator;

  if (!separator) return "\\s*";

  if (separator.match(/^\s+$/)) {
    return "\\s";
  }

  if (separator === ".") {
    return "\\.";
  }

  return separator;
};

export const isStringOnlyNumber = (
  value: string,
  format?: ValueFormat
): boolean => {
  const thousandsSeparator = getThousandsSeparator(format);
  const regexp = new RegExp(
    `^(^-*\\d+(?:\\d|${thousandsSeparator})+)(,|\\.){0,1}(\\d*)$`,
    "gm"
  );
  return !!value.match(regexp);
};

export const doesStringContainsNumber = (
  value: string,
  format?: ValueFormat
): boolean => {
  const thousandsSeparator = getThousandsSeparator(format);
  const regexp = new RegExp(
    `(\\s|^)(-?\\d+(?:\\d|${thousandsSeparator})*)(,|\\.){0,1}(\\d*)`
  );
  return !!value.match(regexp);
};

export const getNumberFromString = (
  value: string,
  format?: ValueFormat
): string => {
  const thousandsSeparator = getThousandsSeparator(format);
  const regexp = new RegExp(
    `(\\s|^)(-?\\d+(?:\\d|${thousandsSeparator})*)(,|\\.){0,1}(\\d*)`
  );
  const matchResult = value.match(regexp);

  if (!matchResult) return value;

  const resultString = matchResult[0];

  return resultString.trim();
};

const getKeyForVocab = (vocabulary: Record<string, string>): string => {
  const numKey = Object.keys(vocabulary).length + 1;
  return `NUM-${numKey}`;
};

type RetVal = {
  encodedString: string;
  vocabulary: Record<string, string>;
};
const encodeInputString = (
  value: string,
  separator: string,
  maxDecimalCount: number,
  vocabulary: Record<string, string>,
  format?: ValueFormat
): RetVal => {
  if (!doesStringContainsNumber(value, format))
    return { encodedString: value, vocabulary };

  const key = getKeyForVocab(vocabulary);
  const unformattedNumberString = getNumberFromString(value, format);
  const formattedNumberString = getEnrichedValueWithDelimiterAndZeros(
    unformattedNumberString,
    separator,
    maxDecimalCount
  );

  const updatedEncodeString = value.replace(unformattedNumberString, key);
  const newVocabulary = {
    ...vocabulary,
    [key]: formattedNumberString,
  };

  return encodeInputString(
    updatedEncodeString,
    separator,
    maxDecimalCount,
    newVocabulary,
    format
  );
};

const decodeInputString = (
  inputString: string,
  vocabulary: Record<string, string>
): string =>
  Object.entries(vocabulary).reduce((prev, curr) => {
    const [key, val] = curr;
    return prev.replace(key, val);
  }, inputString);

export const getValueWithAllNumbersEnriched = (
  value: string,
  separator: string,
  maxDecimalCount: number,
  format?: ValueFormat
): string => {
  const { encodedString, vocabulary } = encodeInputString(
    value,
    separator,
    maxDecimalCount,
    {},
    format
  );
  return decodeInputString(encodedString, vocabulary);
};
